import { create } from 'zustand';
import { CostPriceCategory, CostPriceCategoryScope, fetchCostPriceCategoriesByRestaurant } from '../services/costPrice/costPriceCategoriesService';
import { ALL_RESTAURANTS_ID } from './appUserDefaultStore';

interface CostPriceCategoriesStore {
  categories: CostPriceCategory[];
  isLoading: boolean;
  error: string | null;
  currentRestaurantId: string | null;
  load: (restaurantId: string, options?: { force?: boolean }) => Promise<void>;
  byId: (id: string) => CostPriceCategory | undefined;
  // All categories of a given scope ('revenue' for menu items, 'addon' for BYO choices).
  byScope: (scope: CostPriceCategoryScope) => CostPriceCategory[];
  getDefaultCategory: () => CostPriceCategory | undefined;
  reload: () => Promise<void>;
  applyPositions: (updates: Array<{ id: string; position: number }>) => void;
  clear: () => void;
  isDataLoadedFor: (restaurantId: string) => boolean;
}

let categoryLoadGeneration = 0;
let pendingCategoryRestaurantId: string | null = null;

export const useCostPriceCategoriesStore = create<CostPriceCategoriesStore>((set, get) => ({
  categories: [],
  isLoading: false,
  error: null,
  currentRestaurantId: null,
  
  load: async (restaurantId: string, options) => {
    if (get().isLoading && pendingCategoryRestaurantId === restaurantId && !options?.force) return;
    const generation = ++categoryLoadGeneration;
    pendingCategoryRestaurantId = restaurantId;
    
    // Don't load for "All Restaurants"
    if (restaurantId === ALL_RESTAURANTS_ID) {
      set({ 
        categories: [],
        currentRestaurantId: restaurantId,
        isLoading: false,
        error: null
      });
      return;
    }
    
    set({ isLoading: true, error: null,
      ...(get().currentRestaurantId !== restaurantId ? { categories: [], currentRestaurantId: null } : {}),
    });
    
    try {
      const categories = await fetchCostPriceCategoriesByRestaurant(restaurantId);
      if (generation !== categoryLoadGeneration) return;
      
      set({ 
        categories,
        currentRestaurantId: restaurantId,
        isLoading: false,
        error: null
      });
    } catch (error) {
      if (generation !== categoryLoadGeneration) return;
      console.error('Failed to load cost price categories:', error);
      set({ 
        isLoading: false, 
        error: error instanceof Error ? error.message : 'Failed to load cost price categories'
      });
    }
  },
  
  byId: (id: string) => {
    const state = get();
    return state.categories.find(category => category.id === id);
  },

  byScope: (scope: CostPriceCategoryScope) => {
    const state = get();
    return state.categories.filter(category => (category.scope ?? 'revenue') === scope);
  },

  getDefaultCategory: () => {
    const state = get();
    return state.categories.find(category => category.isDefault);
  },
  
  reload: async () => {
    const state = get();
    if (state.currentRestaurantId && state.currentRestaurantId !== ALL_RESTAURANTS_ID) {
      await get().load(state.currentRestaurantId);
    }
  },

  // Optimistically apply new positions locally so reorder UIs update instantly
  // (before the network save completes) — avoids the drag-and-drop snap-back.
  applyPositions: (updates: Array<{ id: string; position: number }>) => {
    const posMap = new Map(updates.map(u => [u.id, u.position]));
    set(state => {
      const next = state.categories.map(c =>
        posMap.has(c.id) ? { ...c, position: posMap.get(c.id) as number } : c
      );
      next.sort((a, b) => a.position - b.position);
      return { categories: next };
    });
  },
  
  clear: () => {
    categoryLoadGeneration++;
    pendingCategoryRestaurantId = null;
    set({ 
      categories: [],
      currentRestaurantId: null,
      isLoading: false,
      error: null
    });
  },

  isDataLoadedFor: (restaurantId: string) => {
    const state = get();
    return state.currentRestaurantId === restaurantId;
  }
}));